/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Calendar, ClipboardCheck, Trash2 } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type Session, type SessionStatus } from '../../lib/api/sessions.js';
import { useRooms } from '../../lib/queries/rooms.js';
import { useCancelSession } from '../../lib/queries/sessions.js';
import { formatIst } from '../../lib/utils/datetime.js';

import { SessionOutcomeModal } from './session-outcome-modal.js';

const STATUS_TONE: Readonly<Record<SessionStatus, BadgeTone>> = {
  scheduled: 'violet',
  in_progress: 'amber',
  completed: 'green',
  cancelled: 'slate',
  no_show: 'slate',
  client_late: 'amber',
  consultant_attended_client_absent: 'rose',
};

/** Sessions still awaiting an outcome — admin can set status / cancel. */
const OPEN_STATUS: ReadonlySet<SessionStatus> = new Set<SessionStatus>([
  'scheduled',
  'in_progress',
]);

/** Whole-minute duration from the scheduled window, or null when open-ended. */
function durationMin(session: Session): number | null {
  if (session.scheduledEnd === null) {
    return null;
  }
  const ms = new Date(session.scheduledEnd).getTime() - new Date(session.scheduledStart).getTime();
  return ms > 0 ? Math.round(ms / 60_000) : null;
}

function Column({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{value}</p>
    </div>
  );
}

interface RowProps {
  readonly session: Session;
  readonly clientName: string;
  readonly therapistName: string;
  readonly roomName: string;
  readonly onDelete: (id: string) => void;
  readonly onOutcome: (session: Session) => void;
}

/** One Figma session row — calendar icon, four columns, duration + status + actions. */
function SessionRow({
  session,
  clientName,
  therapistName,
  roomName,
  onDelete,
  onOutcome,
}: RowProps): JSX.Element {
  const { t } = useTranslation();
  const duration = durationMin(session);
  const remove = useCallback(() => {
    onDelete(session.id);
  }, [onDelete, session.id]);
  const outcome = useCallback(() => {
    onOutcome(session);
  }, [onOutcome, session]);
  const open = OPEN_STATUS.has(session.status);
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <span className="bg-[var(--accent)]/10 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[var(--accent-strong)]">
        <Calendar className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-5">
        <Column label={t('admin.sessions.colClient')} value={clientName} />
        <Column label={t('admin.sessions.colTherapist')} value={therapistName} />
        <Column
          label={t('admin.sessions.colDateTime')}
          value={formatIst(session.scheduledStart, 'dd MMM, h:mm a')}
        />
        <Column label={t('admin.sessions.colType')} value={session.sessionType ?? '—'} />
        <Column label={t('admin.sessions.colRoom')} value={roomName} />
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {duration !== null && (
          <span className="text-sm text-slate-500">
            {t('admin.sessions.durationMin', { count: duration })}
          </span>
        )}
        <Badge label={t(`status.${session.status}`)} tone={STATUS_TONE[session.status]} />
        {open && (
          <button
            type="button"
            onClick={outcome}
            aria-label={t('admin.sessions.setOutcome')}
            className="focus-ring hover:bg-[var(--accent)]/10 rounded-lg p-1.5 text-slate-400 hover:text-[var(--accent-strong)]"
          >
            <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {open && (
          <button
            type="button"
            onClick={remove}
            aria-label={t('admin.sessions.cancelSession')}
            className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-500/10"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

interface ListProps {
  readonly rows: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly therapistNames: ReadonlyMap<string, string>;
}

/** The clinic session schedule as Figma rows, with a cancel-confirm dialog. */
export function SessionsList({ rows, clientNames, therapistNames }: ListProps): JSX.Element {
  const { t } = useTranslation();
  const cancel = useCancelSession();
  const rooms = useRooms();
  const roomNames = new Map((rooms.data ?? []).map((r) => [r.id, r.name]));
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [outcomeSession, setOutcomeSession] = useState<Session | null>(null);
  const close = useCallback(() => {
    setPendingId(null);
  }, []);
  const closeOutcome = useCallback(() => {
    setOutcomeSession(null);
  }, []);
  const onOutcomeOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setOutcomeSession(null);
    }
  }, []);
  const confirm = useCallback(() => {
    if (pendingId !== null) {
      cancel.mutate({ id: pendingId }, { onSettled: close });
    }
  }, [pendingId, cancel, close]);
  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setPendingId(null);
    }
  }, []);

  return (
    <div className="space-y-3">
      {rows.map((s) => (
        <SessionRow
          key={s.id}
          session={s}
          clientName={clientNames.get(s.clientId) ?? '—'}
          therapistName={s.therapistId !== null ? (therapistNames.get(s.therapistId) ?? '—') : '—'}
          roomName={s.roomId !== null ? (roomNames.get(s.roomId) ?? '—') : '—'}
          onDelete={setPendingId}
          onOutcome={setOutcomeSession}
        />
      ))}
      <Modal
        open={pendingId !== null}
        onOpenChange={onOpenChange}
        title={t('admin.sessions.cancelTitle')}
        description={t('admin.sessions.cancelBody')}
        closeLabel={t('common.cancel')}
      >
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={confirm} disabled={cancel.isPending}>
            {t('admin.sessions.cancelConfirm')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={outcomeSession !== null}
        onOpenChange={onOutcomeOpenChange}
        title={t('admin.sessions.outcomeTitle')}
        description={t('admin.sessions.outcomeBody')}
        closeLabel={t('common.cancel')}
      >
        {outcomeSession !== null ? (
          <SessionOutcomeModal session={outcomeSession} onClose={closeOutcome} />
        ) : (
          <span />
        )}
      </Modal>
    </div>
  );
}
